/**
 * /admin/organizations — B2B organization management.
 *
 * Founder view of every Organization on the platform. Search → list →
 * click row → side drawer with members, plan, feature gates (archive /
 * analyzer access), verification + suspend actions.
 *
 * Mirrors AdminUsers.jsx layout for visual consistency.
 *
 * Backend:
 *   GET    /api/admin/v2/organizations/?search=&category=&verified=
 *   GET    /api/admin/v2/organizations/<id>/
 *   POST   /api/admin/v2/organizations/<id>/suspend/
 *   POST   /api/admin/v2/organizations/<id>/reactivate/
 *   POST   /api/admin/v2/organizations/<id>/toggle_archive/
 *   POST   /api/admin/v2/organizations/<id>/toggle_analyzer/
 *   POST   /api/admin/v2/organizations/<id>/mark_verified/
 *   POST   /api/admin/v2/organizations/<id>/unverify/
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import {
  Search, X, RefreshCw, Shield, ShieldOff, CheckCircle2, XCircle,
  Archive, BarChart3, Building2, Mail, Pause, Play, FileText,
  CheckCheck, AlertTriangle, ExternalLink, Receipt,
} from "lucide-react";
import {
  listOrganizations, getOrganization,
  suspendOrganization, reactivateOrganization,
  toggleOrgArchiveAccess, toggleOrgAnalyzerAccess,
  markOrgVerified, unverifyOrg,
  approveOrgDocument, rejectOrgDocument,
  updateOrgSubscriptionOverride,
  issueOrgInvoice,
} from "../../api/owner.api";

const CATEGORIES = [
  { key: "",           label: "All categories" },
  { key: "FRANCHISE",  label: "Franchise" },
  { key: "FEDERATION", label: "Federation" },
  { key: "LEAGUE",     label: "League" },
  { key: "MEDIA",      label: "Media" },
  { key: "OTHER",      label: "Other" },
];

export default function AdminOrganizations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [verified, setVerified] = useState(searchParams.get("verified") || "");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => fetchPage(1), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, verified]);

  useEffect(() => {
    fetchPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Reflect filter state in URL.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (category) next.set("category", category); else next.delete("category");
    if (verified) next.set("verified", verified); else next.delete("verified");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, verified]);

  async function fetchPage(p) {
    setLoading(true);
    try {
      const { data } = await listOrganizations({
        search: search || undefined,
        category: category || undefined,
        verified: verified || undefined,
        page: p,
      });
      setRows(data.results || []);
      setCount(data.count || 0);
      if (p === 1 && page !== 1) setPage(1);
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(count / 25));

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Organizations</h1>
          <p className="text-sm text-text-secondary mt-1">
            {count.toLocaleString("en-IN")} B2B accounts. Click a row to
            manage members, plan, feature gates + verification.
          </p>
        </div>
        <button
          onClick={() => fetchPage(page)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, city, state, contact…"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-input border border-border-default text-sm focus:outline-none focus:border-button-primary"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="text-sm rounded-md bg-input border border-border-default py-2 px-2"
        >
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
        <select
          value={verified}
          onChange={(e) => setVerified(e.target.value)}
          className="text-sm rounded-md bg-input border border-border-default py-2 px-2"
        >
          <option value="">Any verification</option>
          <option value="true">Verified only</option>
          <option value="false">Unverified only</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-table-header">
            <tr className="text-left text-[11px] uppercase tracking-widest text-text-secondary">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2 hidden md:table-cell">Location</th>
              <th className="px-3 py-2 text-right">Members</th>
              <th className="px-3 py-2">Features</th>
              <th className="px-3 py-2 hidden md:table-cell">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-secondary">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-secondary">No organizations match.</td></tr>
            )}
            {rows.map((o) => (
              <tr
                key={o.id}
                onClick={() => setSelectedId(o.id)}
                className="border-t border-border-default hover:bg-bg-secondary cursor-pointer"
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-text-secondary shrink-0" />
                    <div>
                      <div className="font-medium">{o.name}</div>
                      <div className="text-text-secondary text-xs">{o.contact_number || "—"}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-text-secondary">
                  {o.category || "—"}
                </td>
                <td className="px-3 py-2 text-text-secondary text-xs hidden md:table-cell">
                  {[o.city, o.state].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {o.member_count ?? 0}
                </td>
                <td className="px-3 py-2">
                  <FeatureBadges org={o} />
                </td>
                <td className="px-3 py-2 text-text-secondary text-xs hidden md:table-cell">
                  {o.created_at?.slice(0, 10)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-text-secondary">
        <div>Page {page} of {totalPages}</div>
        <div className="flex gap-1">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Prev</button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Next</button>
        </div>
      </div>

      {selectedId && (
        <OrgDrawer
          orgId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => fetchPage(page)}
        />
      )}
    </div>
  );
}

// ─── Feature badges (list row) ───────────────────────────────────────────

function FeatureBadges({ org }) {
  return (
    <div className="flex flex-wrap gap-1">
      {org.is_verified_for_pro_matches && (
        <Pill icon={CheckCircle2} color="emerald" label="Verified" />
      )}
      {org.has_archive_access && (
        <Pill icon={Archive} color="indigo" label="Archive" />
      )}
      {org.has_analyzer_access && (
        <Pill icon={BarChart3} color="purple" label="Analyzer" />
      )}
      {!org.allow_system_data && (
        <Pill icon={Pause} color="rose" label="Suspended" />
      )}
    </div>
  );
}

function Pill({ icon: Icon, color, label }) {
  const COLOR = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    indigo:  "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
    purple:  "bg-purple-500/15 text-purple-300 border-purple-500/30",
    rose:    "bg-rose-500/15 text-rose-300 border-rose-500/30",
    amber:   "bg-amber-500/15 text-amber-300 border-amber-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${COLOR[color]}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ─── Drawer ─────────────────────────────────────────────────────────────

function OrgDrawer({ orgId, onClose, onChanged }) {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const panelRef = useRef(null);
  useDialogDismiss({ open: true, onClose, ref: panelRef });

  async function reload() {
    setLoading(true);
    try {
      const { data } = await getOrganization(orgId);
      setOrg(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function run(key, fn) {
    setBusyKey(key);
    try {
      const { data } = await fn();
      setOrg(data);
      onChanged?.();
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="presentation">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={org?.name || "Organisation"}
        className="relative w-full max-w-md bg-bg-primary border-l border-border-default h-full overflow-y-auto"
      >
        <header className="flex items-start justify-between p-4 border-b border-border-default sticky top-0 bg-bg-primary z-10">
          <div className="min-w-0">
            {loading && !org && <div className="text-sm text-text-secondary">Loading…</div>}
            {org && (
              <>
                <div className="font-semibold truncate flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-text-secondary shrink-0" />
                  {org.name}
                </div>
                <div className="text-xs text-text-secondary mt-0.5">
                  {org.category || "Uncategorised"} · {[org.city, org.state, org.country].filter(Boolean).join(", ") || "Location unknown"}
                </div>
              </>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-bg-secondary">
            <X className="w-4 h-4" />
          </button>
        </header>

        {org && (
          <div className="p-4 space-y-5">

            {/* Feature gates */}
            <section>
              <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                Feature gates
              </h3>
              <div className="space-y-2">
                <Toggle
                  label="Verified for pro matches"
                  desc="Required for Federation-tier features and pro-match scoring."
                  on={org.is_verified_for_pro_matches}
                  busy={busyKey === "verify"}
                  onChange={() => run("verify", () =>
                    org.is_verified_for_pro_matches ? unverifyOrg(orgId) : markOrgVerified(orgId))}
                />
                <Toggle
                  label="Archive access"
                  desc="3,236-match historical archive (B2B-exclusive moat)."
                  on={org.has_archive_access}
                  busy={busyKey === "archive"}
                  onChange={() => run("archive", () => toggleOrgArchiveAccess(orgId))}
                />
                <Toggle
                  label="Analyzer access"
                  desc="zencricketanalyzer.com SPA + analytics endpoints."
                  on={org.has_analyzer_access}
                  busy={busyKey === "analyzer"}
                  onChange={() => run("analyzer", () => toggleOrgAnalyzerAccess(orgId))}
                />
              </div>
            </section>

            {/* Subscription */}
            <section>
              <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                Active subscription
              </h3>
              {org.active_subscription ? (
                <SubscriptionBlock
                  org={org}
                  busy={busyKey === "subscription-override"}
                  onSave={async ({ customPriceInr, contractNotes }) => {
                    await run("subscription-override", () =>
                      updateOrgSubscriptionOverride(org.id, { customPriceInr, contractNotes }));
                  }}
                />
              ) : (
                <div className="text-xs text-text-secondary">No active plan.</div>
              )}
              {org.bespoke_pricing_notes && (
                <div className="rounded border border-amber-500/30 bg-amber-500/10 p-2 text-xs mt-2">
                  <div className="text-amber-300 font-semibold mb-0.5">Bespoke pricing notes (legacy field)</div>
                  <div className="text-amber-100">{org.bespoke_pricing_notes}</div>
                </div>
              )}
            </section>

            {/* Invoices (G9) */}
            {org.active_subscription && (
              <InvoicesSection
                org={org}
                busy={busyKey === "invoice-issue"}
                onIssue={async (payload) => {
                  await run("invoice-issue", () => issueOrgInvoice(org.id, payload));
                }}
              />
            )}

            {/* Verification documents */}
            <VerificationDocsSection
              org={org}
              busyKey={busyKey}
              onApprove={async (docId, note) => {
                await run(`doc-${docId}-approve`, () => approveOrgDocument(org.id, docId, note));
              }}
              onReject={async (docId, note) => {
                await run(`doc-${docId}-reject`, () => rejectOrgDocument(org.id, docId, note));
              }}
            />

            {/* Archive request — from onboarding step 4 */}
            {org.onboarding?.archive_requested && (
              <ArchiveRequestSection
                org={org}
                busy={busyKey === "archive"}
                onGrant={() => run("archive", () => toggleOrgArchiveAccess(org.id))}
              />
            )}

            {/* Members */}
            <section>
              <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                Members ({(org.members || []).length})
              </h3>
              <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                {(org.members || []).length === 0 && (
                  <div className="text-xs text-text-secondary">No members yet.</div>
                )}
                {(org.members || []).map((m) => (
                  <div key={m.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-bg-secondary/40">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono truncate">{m.email}</div>
                      <div className="text-[10px] text-text-secondary">
                        {m.full_name} · {m.role || "—"} · joined {m.date_joined?.slice(0, 10)}
                      </div>
                    </div>
                    {!m.is_active && (
                      <span className="text-red-400 text-[10px]">suspended</span>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Associates — informational roster. These users are
                grouped under this org for visibility but their request
                scope stays USER (personal data). Backed by the
                Organization.associates M2M, distinct from the
                User.organization FK that drives scope. */}
            <section>
              <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2 flex items-center gap-2">
                Associates ({(org.associates || []).length})
                <span
                  className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-sky-500/40 bg-sky-500/15 text-sky-300 normal-case"
                  title="Informational only — these users' data stays in their personal (USER) scope. Add via the M2M field; does NOT change permissions."
                >
                  personal scope
                </span>
              </h3>
              <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                {(org.associates || []).length === 0 && (
                  <div className="text-xs text-text-secondary">
                    No associates listed.
                  </div>
                )}
                {(org.associates || []).map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-bg-secondary/40">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono truncate">{a.email}</div>
                      <div className="text-[10px] text-text-secondary">
                        {a.full_name} · joined {a.date_joined?.slice(0, 10)}
                      </div>
                    </div>
                    {!a.is_active && (
                      <span className="text-red-400 text-[10px]">suspended</span>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Outstanding invites */}
            {org.invites?.length > 0 && (
              <section>
                <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                  Pending invites ({org.invites.length})
                </h3>
                <div className="space-y-1">
                  {org.invites.map((i) => (
                    <div key={i.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-bg-secondary/40">
                      <span className="font-mono truncate flex items-center gap-1">
                        <Mail className="w-3 h-3 text-text-secondary" />
                        {i.email}
                      </span>
                      <span className={i.is_expired ? "text-red-400" : "text-text-secondary"}>
                        {i.is_expired ? "expired" : i.role}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

          </div>
        )}

        {/* Sticky action footer */}
        {org && (
          <footer className="p-4 border-t border-border-default sticky bottom-0 bg-bg-primary space-y-2">
            {org.allow_system_data ? (
              <button
                disabled={busyKey === "suspend"}
                onClick={() => {
                  if (!confirm(`Suspend ${org.name}? Their data feeds, archive access and analyzer access will revoke immediately. Members stay logged in but lose feature access.`)) return;
                  run("suspend", () => suspendOrganization(orgId));
                }}
                className="w-full px-3 py-2 rounded border border-rose-500/40 bg-rose-500/10 text-rose-300 text-sm hover:bg-rose-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Pause className="w-4 h-4" /> Suspend organization
              </button>
            ) : (
              <button
                disabled={busyKey === "suspend"}
                onClick={() => run("suspend", () => reactivateOrganization(orgId))}
                className="w-full px-3 py-2 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm hover:bg-emerald-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4" /> Reactivate organization
              </button>
            )}
          </footer>
        )}
      </aside>
    </div>
  );
}

function SubscriptionBlock({ org, busy, onSave }) {
  const sub = org.active_subscription;
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(sub.custom_price_inr ?? "");
  const [notes, setNotes] = useState(sub.contract_notes || "");
  // Reset local form when the underlying sub changes (e.g. after save reload).
  useEffect(() => {
    setPrice(sub.custom_price_inr ?? "");
    setNotes(sub.contract_notes || "");
  }, [sub.id, sub.custom_price_inr, sub.contract_notes]);

  async function handleSave() {
    const customPriceInr =
      price === "" || price === null
        ? null
        : Number(price);
    await onSave({ customPriceInr, contractNotes: notes });
    setEditing(false);
  }

  return (
    <div className="rounded border border-border-default p-3 text-sm space-y-2">
      <div>
        <div className="font-medium">{sub.plan_name}</div>
        <div className="text-xs text-text-secondary mt-0.5">
          {sub.plan_code} · {sub.status}
          {sub.payment_provider && ` · via ${sub.payment_provider}`}
        </div>
        {sub.period_end && (
          <div className="text-xs text-text-secondary">
            Period ends {sub.period_end.slice(0, 10)}
          </div>
        )}
      </div>

      {/* Custom price (B2B override) */}
      <div className="border-t border-border-default pt-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-widest text-text-secondary">
            B2B custom pricing
          </span>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-[11px] text-button-primary hover:underline"
            >
              Edit
            </button>
          )}
        </div>

        {!editing ? (
          <div>
            {sub.custom_price_inr != null ? (
              <div className="font-mono text-base text-emerald-300">
                ₹{Number(sub.custom_price_inr).toLocaleString("en-IN")}
                <span className="text-[10px] text-text-secondary ml-2">override</span>
              </div>
            ) : (
              <div className="text-xs text-text-secondary italic">
                Using plan standard price.
              </div>
            )}
            {sub.contract_notes && (
              <div className="text-xs text-text-secondary mt-1 whitespace-pre-wrap">
                {sub.contract_notes}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-text-secondary">
                Custom price (INR) — blank = use plan default
              </label>
              <input
                type="number"
                min={0}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="e.g. 49999"
                className="w-full px-2 py-1.5 rounded bg-bg-secondary border border-border-default text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-text-secondary">
                Contract notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Discount reason, start date, special terms…"
                className="w-full px-2 py-1.5 rounded bg-bg-secondary border border-border-default text-xs"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={busy}
                className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50"
              >
                {busy ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setPrice(sub.custom_price_inr ?? "");
                  setNotes(sub.contract_notes || "");
                }}
                className="px-3 py-1 rounded border border-border-default text-xs hover:bg-bg-secondary"
              >
                Cancel
              </button>
            </div>
            <div className="text-[10px] text-text-secondary">
              Saved values surface on the B2B invoice and stay with this
              subscription until cleared.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoicesSection({ org, busy, onIssue }) {
  const invoices = org.invoices || [];
  const sub = org.active_subscription;
  const defaultPrice = sub?.custom_price_inr ?? "";
  const [open, setOpen] = useState(false);
  const [taxableInr, setTaxableInr] = useState(defaultPrice);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [lineDescription, setLineDescription] = useState("");

  useEffect(() => {
    if (open) setTaxableInr(defaultPrice);
  }, [open, defaultPrice]);

  async function handleIssue() {
    await onIssue({
      taxableInr: Number(taxableInr) || 0,
      periodStart, periodEnd, lineDescription,
    });
    setOpen(false);
    setTaxableInr(defaultPrice);
    setPeriodStart(""); setPeriodEnd("");
    setLineDescription("");
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-widest text-text-secondary">
          Invoices ({invoices.length})
        </h3>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 text-[11px] text-button-primary hover:underline"
          >
            <Receipt className="w-3.5 h-3.5" /> Issue invoice
          </button>
        )}
      </div>

      {open && (
        <div className="rounded border border-button-primary/40 bg-button-primary/5 p-3 space-y-2 text-sm mb-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-text-secondary">
                Taxable amount (INR) *
              </label>
              <input
                type="number"
                min={1}
                value={taxableInr}
                onChange={(e) => setTaxableInr(e.target.value)}
                className="w-full px-2 py-1.5 rounded bg-bg-secondary border border-border-default text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-text-secondary">
                Line description (optional)
              </label>
              <input
                value={lineDescription}
                onChange={(e) => setLineDescription(e.target.value)}
                placeholder="defaults to plan + period"
                className="w-full px-2 py-1.5 rounded bg-bg-secondary border border-border-default text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-text-secondary">
                Period start
              </label>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full px-2 py-1.5 rounded bg-bg-secondary border border-border-default text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-text-secondary">
                Period end
              </label>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full px-2 py-1.5 rounded bg-bg-secondary border border-border-default text-xs"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleIssue}
              disabled={busy || !taxableInr || Number(taxableInr) <= 0}
              className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50"
            >
              {busy ? "Issuing…" : "Issue + render PDF"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-1 rounded border border-border-default text-xs hover:bg-bg-secondary"
            >
              Cancel
            </button>
          </div>
          <div className="text-[10px] text-text-secondary">
            Allocates next FY sequence, renders PDF, uploads to S3, sets status=ISSUED.
            GST split computed from issuer + customer state.
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="text-xs text-text-secondary">No invoices issued yet.</div>
      ) : (
        <div className="space-y-1">
          {invoices.map((inv) => (
            <div key={inv.id} className="flex items-center gap-2 text-xs px-3 py-2 rounded bg-bg-secondary/40">
              <Receipt className="w-3.5 h-3.5 text-text-secondary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono truncate">{inv.number}</div>
                <div className="text-[10px] text-text-secondary">
                  {inv.date} · {inv.status} · ₹{inv.total_inr?.toLocaleString("en-IN")}
                  {inv.period_start && inv.period_end && (
                    ` · ${inv.period_start} → ${inv.period_end}`
                  )}
                </div>
              </div>
              {inv.pdf_url && (
                <a href={inv.pdf_url} target="_blank" rel="noreferrer"
                   className="text-button-primary hover:underline inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> PDF
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function VerificationDocsSection({ org, busyKey, onApprove, onReject }) {
  const docs = org.verification_documents || [];
  const [noteByDoc, setNoteByDoc] = useState({});
  if (docs.length === 0) {
    return (
      <section>
        <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2">
          Verification documents
        </h3>
        <div className="text-xs text-text-secondary">No documents uploaded yet.</div>
      </section>
    );
  }
  const pending = docs.filter((d) => d.status === "PENDING").length;
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-widest text-text-secondary">
          Verification documents
        </h3>
        {pending > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">
            {pending} pending
          </span>
        )}
      </div>
      <div className="space-y-2">
        {docs.map((d) => {
          const note = noteByDoc[d.id] || "";
          const approving = busyKey === `doc-${d.id}-approve`;
          const rejecting = busyKey === `doc-${d.id}-reject`;
          const busy = approving || rejecting;
          return (
            <div key={d.id} className={`rounded border p-3 text-sm ${
              d.status === "APPROVED" ? "border-emerald-500/30 bg-emerald-500/5"
              : d.status === "REJECTED" ? "border-rose-500/30 bg-rose-500/5"
              : "border-border-default bg-bg-secondary/30"
            }`}>
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-text-secondary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                    <span className="truncate">{d.original_filename || d.doc_type}</span>
                    <span className="text-[10px] uppercase tracking-wider text-text-secondary">
                      {d.doc_type}
                    </span>
                  </div>
                  <div className="text-[11px] text-text-secondary mt-0.5">
                    Uploaded by {d.uploaded_by_email} · {d.uploaded_at?.slice(0, 10)}
                    {d.preview_url && (
                      <a href={d.preview_url} target="_blank" rel="noreferrer"
                         className="ml-2 inline-flex items-center gap-1 text-button-primary hover:underline">
                        <ExternalLink className="w-3 h-3" /> view
                      </a>
                    )}
                  </div>
                  {d.reviewer_note && (
                    <div className="text-[11px] text-text-secondary italic mt-1">
                      Note: {d.reviewer_note}
                    </div>
                  )}
                </div>
                <DocStatusBadge status={d.status} />
              </div>

              {d.status === "PENDING" && (
                <div className="mt-2 space-y-2">
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNoteByDoc((s) => ({ ...s, [d.id]: e.target.value }))}
                    placeholder="Reviewer note (required for reject)"
                    className="w-full px-2 py-1 rounded bg-bg-secondary border border-border-default text-xs"
                  />
                  <div className="flex gap-2">
                    <button
                      disabled={busy}
                      onClick={() => onApprove(d.id, note)}
                      className="flex-1 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1"
                    >
                      <CheckCheck className="w-3.5 h-3.5" />
                      {approving ? "Approving…" : "Approve"}
                    </button>
                    <button
                      disabled={busy || !note.trim()}
                      onClick={() => onReject(d.id, note)}
                      className="flex-1 px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      {rejecting ? "Rejecting…" : "Reject"}
                    </button>
                  </div>
                  <div className="text-[10px] text-text-secondary">
                    Reject requires a reviewer note. Both actions email the org owner.
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function DocStatusBadge({ status }) {
  const C = {
    PENDING:  "bg-amber-500/20 text-amber-200 border-amber-500/40",
    APPROVED: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
    REJECTED: "bg-rose-500/20 text-rose-200 border-rose-500/40",
  };
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border shrink-0 ${C[status]}`}>
      {status}
    </span>
  );
}

function ArchiveRequestSection({ org, busy, onGrant }) {
  const granted = !!org.has_archive_access;
  return (
    <section>
      <h3 className="text-xs uppercase tracking-widest text-text-secondary mb-2">
        Archive access request
      </h3>
      <div className={`rounded border p-3 ${
        granted
          ? "border-emerald-500/40 bg-emerald-500/10"
          : "border-indigo-500/40 bg-indigo-500/10"
      }`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Archive className={`w-4 h-4 ${granted ? "text-emerald-300" : "text-indigo-300"}`} />
            {granted ? "Archive access granted" : "Customer requested access"}
          </div>
          {!granted && (
            <button
              disabled={busy}
              onClick={onGrant}
              className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              {busy ? "Granting…" : "Grant archive access"}
            </button>
          )}
        </div>
        {org.onboarding?.archive_request_note && (
          <div className="text-xs text-text-secondary italic mt-2 whitespace-pre-wrap">
            "{org.onboarding.archive_request_note}"
          </div>
        )}
      </div>
    </section>
  );
}

function Toggle({ label, desc, on, busy, onChange }) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onChange}
      className={`w-full text-left rounded border p-3 transition-colors disabled:opacity-50 ${
        on
          ? "border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10"
          : "border-border-default bg-bg-primary hover:bg-bg-secondary"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium flex items-center gap-2">
          {on ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-text-secondary" />}
          {label}
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider ${on ? "text-emerald-300" : "text-text-secondary"}`}>
          {on ? "ON" : "OFF"}
        </span>
      </div>
      <div className="text-xs text-text-secondary mt-1">{desc}</div>
    </button>
  );
}
